/* eslint no-mixed-operators: "off"*/

import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import styled from 'styled-components/macro';
import { useAsync } from 'react-use';

import { OpenRejectToPrevious, HistoryRecord, Task } from 'models/response';

import selectors from 'state/selectors';
import service from 'services';
import utils from 'utils';
import actions from 'state/actions';

import TextArea from 'components/Input/Textarea';
import Modal from 'components/Modal';
import CheckGrouper from 'components/Grouper/CheckGrouper';
import Table from 'components/Table';
import { useHistory } from 'react-router';
import { fontSizePalette, fontWeightPalette } from 'utils/styleConstants';

const Section = styled.div`
  margin: 20px 0;
`;

const Title = styled.p`
  font-family: ${({ theme }) => theme.fontFamily};
  font-size: ${fontSizePalette.medium};
  font-weight: ${fontWeightPalette.medium};
  letter-spacing: -0.5px;
  line-height: 19px;
  margin-right: 8px;
  display: inline-block;
`;
const Label = styled.p`
  display: inline-block;
  margin-bottom: 5px;
`;

const columns = [
  { accessor: 'username', Header: 'User' },
  { accessor: 'name', Header: 'Name' },
];

const RejectPrevious = () => {
  const t = utils.intl.useTranslator();
  const dispatch = useDispatch();
  const protocol = useSelector(selectors.documents.selectActiveDocumentID);
  const [openRejectPreviousResponse, setOpenRejectPreviousResponse] = useState<OpenRejectToPrevious>();
  const [rowsHistory, setRowsHistory] = useState<HistoryRecord[]>([]);
  const [note, setNote] = useState<string>('');
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const history = useHistory();
  const userPreference = useSelector(selectors.user.getUserPreference);

  useAsync(async () => {
    try {
      if (protocol) {
        const { data } = await service.openRejectToPrevious(protocol);
        setRowsHistory(data.wfHistory);
        setOpenRejectPreviousResponse(data);
      }
    } catch (err) {
      console.error(err);
    }
  }, []);

  const updateSelectedTask = (isActive?: boolean, task?: Task) => {
    if (isActive && task) {
      setSelectedTask(task);
    } else {
      setSelectedTask(null);
    }
  };

  const storicoColumns = [
    {
      accessor: 'causalCode',
      Header: t('causalCode'),
    },
    { accessor: 'note', Header: t('note') },
    { accessor: 'protocol', Header: t('protocol') },
    {
      accessor: 'rejectionReason',
      Header: t('rejectionReason'),
      Cell: ({ value }: { value: null | string }) => value ?? '',
    },
    {
      accessor: 'sendDate',
      Header: t('sendDate'),
      Cell: ({ value }: { value: number }) => utils.date.convertDate(value, userPreference.timeZone ?? 'UTC'),
    },
    {
      accessor: 'type',
      Header: t('type'),
    },
    {
      accessor: 'typeDescription',
      Header: t('typeDescription'),
    },
  ];

  const onNoteChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNote(e.target.value);
  };

  const handleReject = async () => {
    try {
      if (protocol && selectedTask) {
        await service.closeRejectToPrevious({
          protocol,
          idPreviousTask: selectedTask.idTaskDefinition || 0,
          note,
        });
        dispatch(actions.modal.closeModal());
        dispatch(actions.documents.removeRows([protocol]));
        if (utils.documents.isOpenDocumentView()) dispatch(actions.documents.openNextDocument(history));
        utils.app.notify('success', t('document_successfully_rejected'));
      }
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <>
      <Modal.Header title={t('reject-previous-title')} subtitle={t('reject-previous-desc')} />
      <Modal.Content>
        {rowsHistory.length ? (
          <>
            <Title>{t('storico')}</Title>
            <Table
              id="reject-previous-history-table"
              hasPagination
              hasSort
              columns={storicoColumns}
              rows={rowsHistory}
            />
          </>
        ) : null}
        {openRejectPreviousResponse ? (
          <>
            <Title>{t('recipient')}</Title>
            {openRejectPreviousResponse.previousTasks.length ? (
              openRejectPreviousResponse.previousTasks.map((task, index: number) => (
                <CheckGrouper
                  onChange={(isActive) => updateSelectedTask(isActive, task)}
                  isSelected={task.idTask === selectedTask?.idTask ? true : false}
                  key={`${index}-${task.idTask}`}
                  selectable
                  title={task.taskName}
                >
                  {task.groups.map((group, index: number) => {
                    const rows = group.users.map((user) => ({ username: user.username, name: user.name }));

                    return (
                      <CheckGrouper
                        customMargin={'0 0 10px 0'}
                        title={group.groupName}
                        key={`${index}-${group.idGroup}-${task.idTask}`}
                        selectable={false}
                      >
                        <Table
                          id="reject-previous-table"
                          key={group.idGroup}
                          columns={columns}
                          rows={rows}
                          rowId="idUser"
                          hasSort
                          light
                        />
                      </CheckGrouper>
                    );
                  })}
                </CheckGrouper>
              ))
            ) : (
              <Label>{t('no-previous-task')}</Label>
            )}
          </>
        ) : null}
        <Section>
          <Title>{t('note')}</Title>
          <TextArea value={note} name="Nota" placeholder={t('note here')} onChange={onNoteChange} />
        </Section>
      </Modal.Content>
      <Modal.Footer
        confirmText={t('continue')}
        confirmAction={handleReject}
        confirmDisabled={(openRejectPreviousResponse?.noteMandatory && !note) || !selectedTask}
      />
    </>
  );
};

export default RejectPrevious;
